// Polls GET /api/runs/{id}/waveform_live for the currently-integrating run and
// folds each cursor into a LiveWaveformAccumulator. Returns the accumulated
// state; the caller (LiveStudyPanel) hands it to LiveRunChart for drawing.
//
// The polling is advisory: it stops as soon as the endpoint reports
// available:false or a terminal status arrives, or on any transport/parse
// error. Once stopped, the completed-run overlay takes over. Passing runId
// null disables the hook entirely (no in-flight run to watch).
import { useEffect, useRef, useState } from "react";

import {
  fetchLiveWaveform,
  isLiveStatusTerminal,
  LiveWaveformAccumulator,
  type LiveChannelArrays,
  type LiveGhost,
  type LiveR2,
  type LiveWaveformStatus,
} from "../api/liveWaveform";

export type LiveWaveformState = {
  runId: number | null;
  status: LiveWaveformStatus | null;
  durationS: number | null;
  simTimeS: number;
  timeS: number[]; // seconds
  channels: LiveChannelArrays; // mm
  ghost: LiveGhost | null; // mm, from the recorded truth (7-post only)
  liveR2: LiveR2 | null;
  // Increments on every folded cursor; a stable append signal for the chart
  // (the underlying arrays are mutated in place, so their identity does not
  // change and cannot be used as an effect dependency).
  sampleCount: number;
  // True while a poll loop is live for `runId`.
  active: boolean;
};

const DEFAULT_POLL_MS = 1000;

export function useLiveWaveform(
  runId: number | null,
  opts: { pollMs?: number } = {},
): LiveWaveformState {
  const pollMs = opts.pollMs ?? DEFAULT_POLL_MS;
  const accRef = useRef(new LiveWaveformAccumulator());
  const stoppedRef = useRef(true);

  // Reset synchronously on run change so a render never exposes the previous
  // run's samples under the new runId. The effect below also resets (the two
  // are idempotent); doing it here closes the one-render window between the
  // runId prop changing and the effect firing.
  if (runId != null && accRef.current.runId !== runId) {
    accRef.current.reset(runId);
  }
  // Re-render pump: bumped on reset / ingest / stop so the render reads the
  // latest accumulator fields.
  const [, setTick] = useState(0);
  const bump = () => setTick((n) => n + 1);

  useEffect(() => {
    if (runId == null) {
      stoppedRef.current = true;
      return;
    }
    accRef.current.reset(runId);
    stoppedRef.current = false;
    bump();

    const ctrl = new AbortController();
    let handle: ReturnType<typeof setInterval> | undefined;

    const stop = () => {
      stoppedRef.current = true;
      if (handle !== undefined) {
        clearInterval(handle);
        handle = undefined;
      }
    };

    const tick = async () => {
      if (stoppedRef.current || ctrl.signal.aborted) return;
      try {
        const payload = await fetchLiveWaveform(
          runId,
          accRef.current.nextSince,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        if (!payload.available) {
          // No live stream (run terminal already, or streaming disabled).
          stop();
          bump();
          return;
        }
        accRef.current.ingest(payload);
        bump();
        if (isLiveStatusTerminal(payload.status)) stop();
      } catch {
        if (ctrl.signal.aborted) return;
        // Best-effort: give up on any transport/parse error. The completed-run
        // overlay is the fallback surface.
        stop();
        bump();
      }
    };

    // Poll on the interval only (no immediate poll): a run takes tens of
    // seconds and the first frame arriving one interval late is imperceptible,
    // while a synchronous first fetch would complicate teardown in tests.
    handle = setInterval(() => void tick(), pollMs);
    return () => {
      ctrl.abort();
      if (handle !== undefined) clearInterval(handle);
    };
  }, [runId, pollMs]);

  const acc = accRef.current;
  return {
    runId: acc.runId,
    status: acc.status,
    durationS: acc.durationS,
    simTimeS: acc.simTimeS,
    timeS: acc.timeS,
    channels: acc.channels,
    ghost: acc.ghost,
    liveR2: acc.liveR2,
    sampleCount: acc.timeS.length,
    active: runId != null && !stoppedRef.current,
  };
}
